import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { X, Upload, Plus, Loader2 } from 'lucide-react';
import { useApiMutation } from '../../hooks/useApi';
import { propertyAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const AddPropertyForm = ({ onClose, onSuccess, initialData = null }) => {
  const isEditing = !!initialData;
  
  const [formData, setFormData] = useState(initialData ? {
    name: initialData.name || '',
    address: initialData.address || '',
    units: initialData.units || '',
    occupied_units: initialData.occupied_units || '',
    total_value: initialData.total_value || '',
    monthly_income: initialData.monthly_income || '',
    status: initialData.status || 'active'
  } : {
    name: '',
    address: '',
    units: '',
    occupied_units: '',
    total_value: '',
    monthly_income: '',
    status: 'active'
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  const { mutate, loading, error } = useApiMutation();
  const { toast } = useToast();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setSelectedImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setSelectedImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Convert string values to numbers where needed
      const propertyData = {
        ...formData,
        units: parseInt(formData.units),
        occupied_units: parseInt(formData.occupied_units || 0),
        total_value: parseFloat(formData.total_value),
        monthly_income: parseFloat(formData.monthly_income)
      };

      let property;
      if (isEditing) {
        // Update existing property
        property = await mutate(propertyAPI.update, initialData.id, propertyData);
      } else {
        // Create new property
        property = await mutate(propertyAPI.create, propertyData);
      }
      
      // Upload images if any
      if (selectedImages.length > 0) {
        const propertyId = property.id || property._id;
        
        if (!propertyId) {
          console.error('ERROR: Property ID is missing!', property);
          toast({
            title: "Image Upload Failed",
            description: "Property ID is missing. Please try again or contact support.",
            variant: "destructive",
          });
          setUploadingImages(false);
          return;
        }
        
        console.log(`Uploading ${selectedImages.length} images for property ID: ${propertyId}`);
        setUploadingImages(true);
        
        for (const imageData of selectedImages) {
          try {
            console.log(`Uploading image: ${imageData.name} to property ${propertyId}`);
            const result = await mutate(propertyAPI.uploadImage, propertyId, imageData.file);
            console.log('Image upload result:', result);
          } catch (imageError) {
            console.error('Failed to upload image:', imageError);
            toast({
              title: "Image Upload Warning",
              description: `Failed to upload ${imageData.name}: ${imageError.message}`,
              variant: "destructive",
            });
          }
        }
        setUploadingImages(false);
        console.log('All images uploaded successfully');
      }

      toast({
        title: "Success!",
        description: `Property "${property.name}" has been ${isEditing ? 'updated' : 'created'} successfully.`,
      });

      onSuccess?.();
      onClose();

    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to create property. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{isEditing ? 'Edit Property' : 'Add New Property'}</CardTitle>
          <Button variant="ghost" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Property Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Sunset Apartments"
                  required
                />
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="maintenance">Under Maintenance</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="address">Address *</Label>
              <Textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Full property address"
                rows="2"
                required
              />
            </div>

            {/* Property Images Section - Moved Higher for Better Visibility */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <Label className="text-base font-semibold">Property Images</Label>
              <p className="text-xs text-gray-600 mb-3">Upload photos of the property (optional but recommended)</p>
              <div className="mt-2">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="flex items-center justify-center w-full p-6 border-2 border-dashed border-blue-300 rounded-lg hover:border-blue-400 hover:bg-blue-100 cursor-pointer transition-colors bg-white"
                >
                  <div className="text-center">
                    <Upload className="h-10 w-10 text-blue-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-700">Click to upload property images</p>
                    <p className="text-xs text-gray-500 mt-1">JPG, PNG up to 10MB each • Multiple files allowed</p>
                  </div>
                </label>
              </div>

              {selectedImages.length > 0 && (
                <div className="mt-4 grid grid-cols-3 gap-4">
                  {selectedImages.map((image, index) => (
                    <div key={index} className="relative">
                      <img
                        src={image.preview}
                        alt={image.name}
                        className="w-full h-24 object-cover rounded-lg border-2 border-blue-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-lg"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="units">Total Units *</Label>
                <Input
                  id="units"
                  name="units"
                  type="number"
                  value={formData.units}
                  onChange={handleInputChange}
                  placeholder="e.g., 24"
                  min="1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="occupied_units">Occupied Units</Label>
                <Input
                  id="occupied_units"
                  name="occupied_units"
                  type="number"
                  value={formData.occupied_units}
                  onChange={handleInputChange}
                  placeholder="e.g., 18"
                  min="0"
                  max={formData.units || 999}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="total_value">Total Property Value *</Label>
                <Input
                  id="total_value"
                  name="total_value"
                  type="number"
                  step="0.01"
                  value={formData.total_value}
                  onChange={handleInputChange}
                  placeholder="e.g., 2500000"
                  min="0"
                  required
                />
              </div>
              <div>
                <Label htmlFor="monthly_income">Monthly Income *</Label>
                <Input
                  id="monthly_income"
                  name="monthly_income"
                  type="number"
                  step="0.01"
                  value={formData.monthly_income}
                  onChange={handleInputChange}
                  placeholder="e.g., 36000"
                  min="0"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading || uploadingImages}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading || uploadingImages ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {uploadingImages ? 'Uploading Images...' : isEditing ? 'Updating Property...' : 'Creating Property...'}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    {isEditing ? 'Update Property' : 'Create Property'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddPropertyForm;